import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUniversalAccess,
  FaSlidersH,
  FaHandsHelping,
  FaKeyboard,
  FaEye,
  FaCheckCircle,
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const sectionHeadingStyle = {
  fontFamily: "'Poppins', sans-serif",
  fontWeight: 700,
  letterSpacing: '0.05em',
};

const pillars = [
  {
    icon: FaUniversalAccess,
    title: 'Aligned with ADA standards',
    body: 'WCAG 2.2 AA minded fixes that help your site meet U.S. accessibility expectations and cut legal risk.',
  },
  {
    icon: FaSlidersH,
    title: 'Tuned to your site',
    body: 'Options and plans that scale with you, whether you run a local shop site or a multi-page brand hub.',
  },
  {
    icon: FaHandsHelping,
    title: 'We carry the load',
    body: 'Fast to launch. We handle setup, tuning, and the ongoing details so you can stay focused on clients.',
  },
];

const buildPractices = [
  {
    icon: FaKeyboard,
    title: 'Keyboard-first flows',
    body: 'Menus, forms, and booking steps work without a mouse. Tab order, focus states, and Escape or Enter are built in.',
  },
  {
    icon: FaEye,
    title: 'Readable by design',
    body: 'Contrast, typography, and structure that stay clear for low vision, older adults, and busy mobile readers.',
  },
  {
    icon: FaCheckCircle,
    title: 'Tools you can control',
    body: 'An on-site accessibility toolbar lets visitors adjust contrast, text size, motion, and more. Preferences stay in their browser.',
  },
];

export default function Accessibility() {
  const [isDemoPlaying, setIsDemoPlaying] = useState(false);
  const demoVideoRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const playDemo = () => {
    const video = demoVideoRef.current;
    if (!video) return;
    setIsDemoPlaying(true);
    video.play().catch(() => {});
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ backgroundColor: '#accbff' }}>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10"
        style={{ backgroundImage: 'url(/bg1.jpeg)', opacity: 0.35 }}
      />
      <Navbar />

      <main className="flex-1 pt-40 md:pt-44">
        {/* Intro */}
        <section className="px-4 md:px-8 pb-16 md:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <p
              className="text-xs uppercase tracking-wider mb-3 font-semibold"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#ff7a59' }}
            >
              Web accessibility
            </p>
            <h1
              className="text-3xl md:text-5xl font-bold mb-4 text-gray-700"
              style={sectionHeadingStyle}
            >
              Accessibility shaped around your website
            </h1>
            <p
              className="text-base md:text-lg max-w-xl mx-auto mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              Under <strong>U.S. law</strong>, equal access online matters. We build sites more people can use.
            </p>

            <div
              className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 rounded-2xl px-5 py-4 mb-8 text-left shadow-md border"
              style={{
                backgroundColor: 'rgba(255, 252, 240, 0.9)',
                borderColor: 'rgba(255, 122, 89, 0.45)',
              }}
            >
              <span
                className="shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full text-white"
                style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
              >
                Included
              </span>
              <p
                className="text-sm md:text-base font-medium text-center sm:text-left"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                Buy a website with us, and ADA features come with it.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book" className="group">
                <button
                  className="px-8 py-3 text-white font-medium rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                  style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                >
                  Book a consultation
                </button>
              </Link>
              <Link to="/ada-compliant" className="group">
                <button
                  className="px-8 py-3 font-medium rounded-full border-2 border-[#ff7a59] text-gray-700 bg-white/40 backdrop-blur-sm transition-all duration-300 hover:bg-white/60"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Read our statement
                </button>
              </Link>
            </div>
          </div>
        </section>

        {/* Demo placeholder */}
        <section className="px-4 md:px-8 pb-16 md:pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h2
                className="text-2xl md:text-3xl font-bold mb-3 text-gray-700"
                style={sectionHeadingStyle}
              >
                How the ADA Icon Works
              </h2>
              <p
                className="text-sm md:text-base max-w-xl mx-auto"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                Watch how visitors can open the ADA button and adjust the site to fit their needs.
                You can also try the blue button on this page.
              </p>
            </div>

            <div
              id="ada-demo-slot"
              className="relative aspect-video w-full rounded-2xl overflow-hidden border border-white/30 bg-black/10 shadow-xl"
            >
              <video
                ref={demoVideoRef}
                src="/adabuttondemo.mp4"
                className="w-full h-full object-cover"
                controls={isDemoPlaying}
                playsInline
                preload="metadata"
                onEnded={() => setIsDemoPlaying(false)}
              >
                Your browser does not support the video tag.
              </video>
              {!isDemoPlaying && (
                <button
                  type="button"
                  onClick={playDemo}
                  className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 hover:bg-black/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff7a59] focus-visible:ring-inset"
                  aria-label="Play How the ADA Icon Works video"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform">
                    <svg className="w-7 h-7 md:w-8 md:h-8 text-[#ff7a59] ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <p
                    className="mt-4 text-white text-sm md:text-base font-semibold drop-shadow-md"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    How the ADA Icon Works
                  </p>
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Three pillars */}
        <section className="px-4 md:px-8 pb-16 md:pb-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {pillars.map((item, index) => (
              <div
                key={item.title}
                className="ada-teaser-card rounded-2xl border shadow-md p-6 md:p-8 text-center animate-fade-in-up"
                style={{
                  animationDelay: `${index * 100}ms`,
                  backgroundColor: 'rgba(255, 252, 240, 0.72)',
                  borderColor: 'rgba(255, 122, 89, 0.35)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="ada-teaser-icon w-14 h-14 mx-auto mb-5 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: '#ff7a59' }}
                >
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3
                  className="text-lg md:text-xl font-bold mb-3 text-gray-700"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm md:text-base leading-relaxed"
                  style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                >
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* How we build */}
        <section className="px-4 md:px-8 pb-16 md:pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2
                className="text-2xl md:text-3xl font-bold mb-3 text-gray-700"
                style={sectionHeadingStyle}
              >
                How our websites are built to include everyone
              </h2>
              <p
                className="text-sm md:text-base max-w-2xl mx-auto"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                Accessibility is not a sticker we add at the end. It is part of structure, forms,
                motion, and the tools visitors can turn on themselves.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {buildPractices.map((item, index) => (
                <div
                  key={item.title}
                  className="ada-teaser-card rounded-2xl border shadow-md p-6 animate-fade-in-up"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    backgroundColor: 'rgba(255, 252, 240, 0.72)',
                    borderColor: 'rgba(255, 122, 89, 0.35)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <item.icon className="ada-teaser-icon w-6 h-6 mb-4" style={{ color: '#ff7a59' }} />
                  <h3
                    className="font-bold mb-2 text-gray-700"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                  >
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* US ADA law overview */}
        <section className="px-4 md:px-8 pb-16 md:pb-24">
          <div className="max-w-3xl mx-auto bg-white/25 backdrop-blur-sm rounded-2xl border border-white/30 shadow-lg p-6 md:p-10">
            <h2
              className="text-2xl md:text-3xl font-bold mb-4 text-gray-700"
              style={sectionHeadingStyle}
            >
              ADA and websites in the United States
            </h2>
            <div
              className="space-y-4 text-sm md:text-base leading-relaxed"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              <p>
                The Americans with Disabilities Act (ADA) is a civil rights law. Title III covers
                businesses open to the public—often called places of public accommodation. Courts and
                the U.S. Department of Justice have made clear that websites and apps that offer goods
                or services to the public should be usable by people with disabilities, not only
                brick-and-mortar locations.
              </p>
              <p>
                The DOJ points to technical guidelines such as the Web Content Accessibility Guidelines
                (WCAG) as practical help for making sites accessible. For private businesses, WCAG is
                not always spelled out line-by-line in a Title III regulation the way it is for some
                government sites—but settlements, guidance, and lawsuits commonly treat{' '}
                <strong>WCAG Level AA</strong> (today often 2.1 or 2.2) as the working benchmark.
              </p>
              <p>
                State and local government sites (ADA Title II) now have clearer federal rules that
                reference WCAG 2.1 AA, with phased compliance dates. Private businesses still face
                ongoing risk: demand letters and lawsuits over inaccessible sites remain common. Aiming
                for WCAG 2.2 AA is a strong way to stay ahead.
              </p>
              <p>
                Beyond the U.S., the European Accessibility Act (EAA) raises similar expectations for
                many digital products serving EU customers. Building to WCAG helps you prepare for both
                markets.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Official overview:{' '}
                  <a
                    href="https://www.ada.gov/resources/web-guidance/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline"
                    style={{ color: '#ff7a59' }}
                  >
                    ADA.gov — Guidance on Web Accessibility
                  </a>
                </li>
                <li>
                  Technical guidelines:{' '}
                  <a
                    href="https://www.w3.org/WAI/standards-guidelines/wcag/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline"
                    style={{ color: '#ff7a59' }}
                  >
                    W3C WCAG overview
                  </a>
                </li>
              </ul>
              <p className="text-xs opacity-80 pt-2">
                This page is educational, not legal advice. Talk with your attorney about your specific
                risk and obligations.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 md:px-8 pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-2xl md:text-3xl font-bold mb-4 text-gray-700"
              style={sectionHeadingStyle}
            >
              Ready to make your site work for everyone?
            </h2>
            <p
              className="mb-5"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              Tell us about your site. We will map what you need.
            </p>
            <div
              className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-3 rounded-2xl px-5 py-3.5 mb-8 shadow-md border"
              style={{
                backgroundColor: 'rgba(255, 252, 240, 0.9)',
                borderColor: 'rgba(255, 122, 89, 0.45)',
              }}
            >
              <span
                className="shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full text-white"
                style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
              >
                Included
              </span>
              <p
                className="text-sm md:text-base font-medium text-center sm:text-left"
                style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
              >
                Every website we build already comes with ADA features.
              </p>
            </div>
            <Link to="/book" className="group">
              <button
                className="px-10 py-3 text-white font-medium rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
              >
                Schedule a free call
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
